/**
 * Parameter path parsing utilities.
 *
 * Supports dot notation paths for targeting nested parameters in widgets:
 * - Simple: "amplitude"
 * - Element.param: "f.a"
 * - Deep nesting: "elements.f.params.transform.scale"
 * - Array indices: "functions[0].params.a" or "list.0.value"
 * - Wildcards: "elements[*].visible" (for bulk operations)
 */

/**
 * Segment types in a parameter path.
 */
export type PathSegmentType = 'property' | 'index' | 'wildcard';

export interface PathSegment {
  type: PathSegmentType;
  value: string | number;
  raw: string;
}

/**
 * Parsed parameter path with full segment information.
 */
export interface ParsedParamPath {
  /** Full original path */
  raw: string;
  /** Parsed segments */
  segments: PathSegment[];
  /** Root segment (first element/target) */
  root?: string;
  /** Leaf segment (final param name) */
  leaf: string;
  /** Whether path contains array indices */
  hasIndices: boolean;
  /** Whether path contains wildcards */
  hasWildcards: boolean;
}

/**
 * Parse a parameter path with support for:
 * - Dot notation: "element.partA.partB"
 * - Array indices: "elements[0].param" or "list.0.value"
 * - Wildcards: "elements[*].param" or "elements.*.param"
 *
 * @example
 * parseParamPath('f.a')
 * // → { root: 'f', leaf: 'a', segments: [{type:'property',value:'f'}, {type:'property',value:'a'}] }
 *
 * parseParamPath('functions[0].params.a')
 * // → { root: 'functions', leaf: 'a', hasIndices: true, segments: [...] }
 *
 * parseParamPath('elements[*].visible')
 * // → { root: 'elements', leaf: 'visible', hasWildcards: true, segments: [...] }
 *
 * parseParamPath('amplitude')
 * // → { root: undefined, leaf: 'amplitude', segments: [{type:'property',value:'amplitude'}] }
 */
export function parseParamPath(path: string): ParsedParamPath {
  const segments: PathSegment[] = [];

  // Normalize bracket notation to dot notation for splitting
  // "elements[0].param" → "elements.0.param"
  // "elements[*].param" → "elements.*.param"
  const normalized = path.replace(/\[(\d+|\*)\]/g, '.$1');
  const parts = normalized.split('.').filter(Boolean);

  for (const part of parts) {
    if (part === '*') {
      segments.push({ type: 'wildcard', value: '*', raw: '[*]' });
    } else if (/^\d+$/.test(part)) {
      segments.push({ type: 'index', value: parseInt(part, 10), raw: `[${part}]` });
    } else {
      segments.push({ type: 'property', value: part, raw: part });
    }
  }

  const hasIndices = segments.some((s) => s.type === 'index');
  const hasWildcards = segments.some((s) => s.type === 'wildcard');
  const root = segments.length > 1 && segments[0].type === 'property' ? (segments[0].value as string) : undefined;
  const leaf = segments[segments.length - 1]?.value as string;

  return { raw: path, segments, root, leaf, hasIndices, hasWildcards };
}

/**
 * Get value at path from an object.
 * Returns undefined if path doesn't exist.
 * Wildcards return array of all matching values.
 */
export function getValueAtPath(obj: Record<string, unknown>, path: ParsedParamPath | string): unknown {
  const parsed = typeof path === 'string' ? parseParamPath(path) : path;

  let current: unknown = obj;

  for (let i = 0; i < parsed.segments.length; i++) {
    const segment = parsed.segments[i];

    if (current === null || current === undefined) {
      return undefined;
    }

    if (segment.type === 'wildcard') {
      if (!Array.isArray(current)) {
        return undefined;
      }
      // Return array of values from all items
      const remaining = parsed.segments.slice(i + 1);
      if (remaining.length === 0) {
        return current;
      }
      return current.map((item) =>
        getValueAtPath(item, {
          raw: '',
          segments: remaining,
          leaf: parsed.leaf,
          hasIndices: false,
          hasWildcards: false,
        }),
      );
    }

    current = (current as Record<string | number, unknown>)[segment.value];
  }

  return current;
}
